import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import trainingApi from '../../services/trainingApi';

const ScenarioPreviewDialog = ({ open, onClose, scenarioId }) => {
  const [transcript, setTranscript] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !scenarioId) return;

    let cancelled = false;
    const runPreview = async () => {
      setIsLoading(true);
      setError(null);
      setTranscript([]);
      try {
        const response = await trainingApi.previewScenario(scenarioId);
        if (cancelled) return;
        const body = response.data || response;
        const data = body.data || body;
        setTranscript(data.transcript || data.messages || []);
      } catch (err) {
        if (!cancelled) {
          console.error('[ScenarioPreviewDialog] Preview error:', err);
          setError(err.response?.data?.error || err.message || 'Failed to run preview');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    runPreview();
    return () => { cancelled = true; };
  }, [open, scenarioId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>AI-vs-AI Scenario Preview</DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Running AI-vs-AI preview... This may take 30-60 seconds.
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {!isLoading && !error && transcript.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No transcript generated.
          </Typography>
        )}

        {!isLoading && transcript.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {transcript.map((msg, idx) => {
              const isStudent = msg.actor === 'student' || msg.role === 'student';
              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: isStudent ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Box sx={{ maxWidth: '75%' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                      {isStudent ? 'Student (AI)' : 'Patient (AI)'}
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: isStudent ? 'primary.main' : 'grey.100',
                        color: isStudent ? 'primary.contrastText' : 'text.primary',
                        borderRadius: isStudent ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {msg.content || msg.text || ''}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScenarioPreviewDialog;
